import {
  Box,
  BoxProps,
  IconButton,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { SemanticIndicatorColors } from "@/styles/nawy-theme/foundation/colors/semantic";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { SxProps } from "@mui/material";

export type LabelVariant = "primary" | "secondary" | "outlined";

export interface LabelAction {
  icon: React.ReactNode;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export interface LabelProps extends Omit<BoxProps, "variant" | "color"> {
  label: string | React.ReactNode;
  variant?: LabelVariant;
  color?: SemanticIndicatorColors;
  action?: LabelAction;
  showActionButton?: boolean;
}

// Label variant styles - keeping the original styles
const getLabelStyles = (
  variant: LabelVariant = "primary",
  color: SemanticIndicatorColors = "blue"
): SxProps => {
  const semanticColor = nawyTheme.nawy.colors.semantic.indicator[color];
  const semanticColorLight =
    nawyTheme.nawy.colors.semantic.indicator[
      `${color}Light` as keyof typeof nawyTheme.nawy.colors.semantic.indicator
    ];
  const semanticColorDark =
    nawyTheme.nawy.colors.semantic.indicator[
      `${color}Dark` as keyof typeof nawyTheme.nawy.colors.semantic.indicator
    ];

  const primaryLabelStyles = {
    backgroundColor: semanticColor ?? color,
    color: nawyTheme.nawy.colors.semantic.text.onColor,
    border: "none",
  };

  const secondaryLabelStyles = {
    backgroundColor: semanticColor ? semanticColorLight : color,
    color: semanticColor ? semanticColorDark : color,
    border: "none",
  };

  const outlinedLabelStyles = {
    backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
    color: semanticColor ?? color,
    border: `1px solid ${semanticColor ?? color}`,
  };

  const labelVariantStylesMap = new Map<string, any>([
    ["primary", primaryLabelStyles],
    ["secondary", secondaryLabelStyles],
    ["outlined", outlinedLabelStyles],
  ]);

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `0 ${nawyTheme.nawy.spacing.xxs}`,
    height: "20px",
    fontSize:
      nawyTheme.nawy.typography.english.variants.body.xs.regular.fontSize,
    fontWeight:
      nawyTheme.nawy.typography.english.variants.body.xs.regular.fontWeight,
    lineHeight:
      nawyTheme.nawy.typography.english.variants.body.xs.regular.lineHeight,
    borderRadius: nawyTheme.nawy.radius.md,
    fontFamily: "inherit",
    ...labelVariantStylesMap.get(variant),
  };
};

export const Label: React.FC<LabelProps> = ({
  label,
  variant = "primary",
  color = "blue",
  action,
  showActionButton = false,
  ...props
}) => {
  const theme = useTheme();
  const semanticColor =
    nawyTheme.nawy.colors.semantic.indicator[color] ?? color;

  const shouldShowAction = showActionButton && action;

  return (
    <Box sx={getLabelStyles(variant, color)} {...props}>
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.sm.regular,
          textTransform: "capitalize",
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {label}
      </Typography>
      {shouldShowAction && (
        <IconButton
          size="small"
          onClick={action.onClick}
          disabled={action.disabled}
          sx={{
            padding: "0",
            marginLeft: "2px",
            color: semanticColor,
            "&:hover": {
              background: "transparent",
            },
            "&:disabled": {
              opacity: 0.5,
            },
          }}
        >
          {action.isLoading ? <CircularProgress size={14} /> : action.icon}
        </IconButton>
      )}
    </Box>
  );
};

export default Label;
